import React, { useEffect, useState, useRef } from "react";
import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import { useDesktopBreakpoint } from "web/react/hooks/use-desktop-breakpoint/use-desktop-breakpoint";
import BackButton from "web/react/components/buttons/back-button/back-button";
import styles from "./drawer.css";

enum CloseType {
    BACK_CLICK,
    OVERLAY_CLICK,
    CANCEL_CLICK,
}

interface DrawerProps {
    open: boolean;
    title: string;
    children?: JSX.Element;
    id: string;
    onBack: () => void;
    onCancel: () => void;
    onOverlay: () => void;
}

function Drawer({
    open,
    title,
    children,
    id,
    onBack,
    onCancel,
    onOverlay,
}: DrawerProps): JSX.Element {
    let overlayRef = useRef(null);
    let largeLayout = useDesktopBreakpoint();
    let [isClosing, setClosing] = useState(false);

    useEffect(() => {
        document.body.classList.toggle("no-scroll", open);
        return (): void => {
            document.body.classList.remove("no-scroll");
        };
    }, [open]);

    function beforeClose(closeType): void {
        setTimeout(function() {
            setClosing(false);
        }, 400);
        setClosing(true);

        if (closeType == CloseType.BACK_CLICK) {
            onBack();
        } else if (closeType == CloseType.OVERLAY_CLICK) {
            onOverlay();
        } else if (closeType == CloseType.CANCEL_CLICK) {
            onCancel();
        }
    }

    function handleOverlayClick(event): void {
        if (event.target === overlayRef.current) {
            beforeClose(CloseType.OVERLAY_CLICK);
        }
    }

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className={classnames(styles.overlay, {
                [styles.open]: open,
                [styles.closing]: isClosing,
            })}
        >
            <article
                className={styles.drawer}
                role={largeLayout ? "complementary" : "dialog"}
                aria-modal={!largeLayout}
                aria-labelledby={`${id}-drawer-title`}
                aria-describedby={`${id}-drawer-children`}
                aria-hidden={!open && !largeLayout}
            >
                <div className={styles.header}>
                    <div className={styles.back}>
                        <BackButton onClick={(): void => beforeClose(CloseType.BACK_CLICK)} />
                    </div>
                    <h1 className={styles.title} id={`${id}-drawer-title`}>
                        {title}
                    </h1>
                    <button
                        className={styles.cancel}
                        onClick={(): void => beforeClose(CloseType.CANCEL_CLICK)}
                    >
                        {gettext("feed.filters.mobile_cancel_btn_text")}
                    </button>
                </div>
                <div className={styles.content} id={`${id}-drawer-children`}>
                    {children}
                </div>
            </article>
        </div>
    );
}

export default Drawer;
